/**
 * The MetEn house forward price path for a new quick study, as the Economics
 * page's «Load MetEn house path (live anchors)» button loads it: the live path
 * (GET /market/house-forward-path) for the project's years and start year, or,
 * when the server does not answer in time, the static Greek mirror. Never
 * throws: without a path the quick study is created without one.
 */
import { buildHouseForwardPath } from "./houseForwardPath";
import { operatingStartYear } from "../../utils/simulationYears";
import { normalizeMarketZone } from "../../utils/marketSupport";

export const STUDY_FORWARD_PATH_TIMEOUT_MS = 8000;

export async function loadStudyHousePath(config, {
  fetchImpl = typeof fetch === "function" ? fetch : null,
  baseUrl = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, ""),
  timeoutMs = STUDY_FORWARD_PATH_TIMEOUT_MS,
} = {}) {
  const years = Math.max(1, Math.round(Number(config?.feasibility?.settlementPeriod) || 20));
  const startYear = operatingStartYear(config);
  const zone = normalizeMarketZone(config?.biddingZone) || "GR";
  let house = null;
  if (fetchImpl) {
    const abort = typeof AbortController === "undefined" ? null : new AbortController();
    const timer = abort ? setTimeout(() => abort.abort(), timeoutMs) : null;
    try {
      const resp = await fetchImpl(
        `${baseUrl}/market/house-forward-path?years=${years}&startYear=${startYear}&zone=${zone}`,
        abort ? { signal: abort.signal } : undefined,
      );
      if (resp?.ok) house = await resp.json();
    } catch {
      house = null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }
  if (!house || !Array.isArray(house.levelsEurMwh) || !house.levelsEurMwh.length) {
    // The static mirror is Greek only; another zone is never given a Greek path.
    house = zone === "GR" ? buildHouseForwardPath(years, startYear) : null;
  }
  return { house, startYear };
}
